"use client";

import { useEffect } from "react";
import { reportClientError } from "@/lib/error-reporting/client";
import "./globals.css";

/**
 * Last-resort error boundary: replaces the root layout when it (or anything
 * uncaught below it) throws, so it must render its own <html>/<body>. Kept
 * deliberately simple — no theme provider, fonts, or i18n are available here.
 * Route-level errors inside the app shell are handled by (main)/error.tsx.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    reportClientError(error, {
      source: "global-error-boundary",
      digest: error.digest,
    });
  }, [error]);

  return (
    <html lang="en">
      <body className="antialiased">
        <main className="flex min-h-svh items-center justify-center bg-background p-6">
          <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 text-center shadow-sm">
            <div
              aria-hidden
              className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10 text-3xl text-destructive"
            >
              !
            </div>
            <h1 className="text-2xl font-semibold tracking-tight text-foreground">
              Something went wrong
            </h1>
            <p className="mt-3 text-sm text-muted-foreground">
              An unexpected error occurred and has been reported to our team.
              Try again, or head back home.
            </p>
            {error.digest ? (
              <p className="mt-2 text-xs text-muted-foreground/70">
                Error reference: {error.digest}
              </p>
            ) : null}
            <div className="mt-6 flex flex-col gap-2">
              <button
                type="button"
                onClick={reset}
                className="inline-flex h-9 items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
              >
                Try again
              </button>
              {/* /home, RELATIVE by design (AIR-1169): this boundary replaces
                  the root layout, so no provider or server-computed href can
                  reach it, and CHAT_HOST/PORTAL_HOST are server-only env. On
                  the portal host /home is the real home ("/" is shadowed by
                  the static marketing page, which reads as signed-out); on
                  the chat host the middleware 308-bounces /home to the
                  portal host — an extra hop, but it resolves, which is as
                  honest as a last-resort boundary can be. */}
              {/* eslint-disable-next-line @next/next/no-html-link-for-pages --
                  the app shell just crashed; a full document load is more
                  reliable here than client-side routing */}
              <a
                href="/home"
                className="inline-flex h-9 items-center justify-center rounded-md border border-input bg-background px-4 text-sm font-medium text-foreground transition-colors hover:bg-accent"
              >
                Go home
              </a>
            </div>
          </div>
        </main>
      </body>
    </html>
  );
}
